// Copyright (c) 2023 WiseTime. All rights reserved.

import { SearchFilter, ToolbarState } from "./toolbar/slice"
import { SiteId } from "../user-context/slice"
import { equalsById, getMergedFilter, isSearchNotEmpty } from "./search-bar/searchUtils"
import { isTimeRow } from "./timeBlocks"
import { CollapsibleTimeGroup } from "./timeGroups"
import { TimeGroup, TimeRow } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { TimeRowViewFlag } from "../../generated/timeline/toolbar/toolbar_pb"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { ActivityTypeRef } from "../../generated/wisetime/user/type/activity_type_ref_pb"
import { TagCategoryRef } from "../../generated/wisetime/user/type/tag_category_ref_pb"
import { TimeRowId } from "./slices/timeline"

type IsMatchingItem<T> = (
  item: T,
  check: TimeRow.AsObject | TimeGroup.AsObject,
  currentSiteId?: SiteId,
) => boolean | undefined

const isMatchingTag: IsMatchingItem<TagRef.AsObject> = (
  item,
  check,
  currentSiteId,
) => item.siteId === currentSiteId && check.tagsList.some(tag => equalsById(tag, item))

const isMatchingActivityType: IsMatchingItem<ActivityTypeRef.AsObject> = (
  item,
  check,
  currentSiteId,
) => item.siteId === currentSiteId && check.activityTypesList.some(activityType => equalsById(activityType, item))

const isMatchingTagCategory: IsMatchingItem<TagCategoryRef.AsObject> = (
  item,
  check,
  currentSiteId,
) => item.siteId === currentSiteId && check.tagCategoriesList.some(tagCategory => equalsById(tagCategory, item))

export type ViewFlagFilter = (
  timeRow: TimeRow.AsObject,
  currentSiteId: SiteId | undefined,
) => boolean
export const viewFlagFilters: Record<TimeRowViewFlag, ViewFlagFilter> = {
  [TimeRowViewFlag.TAGGED]: (timeRow: TimeRow.AsObject, currentSiteId: SiteId | undefined) =>
    timeRow.tagsList.some(t => t.siteId === currentSiteId),
  [TimeRowViewFlag.UNTAGGED]: (timeRow: TimeRow.AsObject, currentSiteId: SiteId | undefined) =>
    timeRow.tagsList.every(t => t.siteId !== currentSiteId),
  // Skip filtering. We can't remove zero indexed enum value in proto3 or
  // reorder indexes since it may mislead persisted TimeRowViewFlag on users side.
  [TimeRowViewFlag.ERRORED]: () => true,
}

const isMatchingToolbarFilters = (
  timeRow: TimeRow.AsObject,
  toolbar: ToolbarState,
  currentSiteId?: SiteId,
): boolean => {
  if (!currentSiteId) {
    return true
  }
  
  let isMatching = true
  
  const viewFlags = toolbar.viewFlags.filter(viewFlag => (
    Object.values(TimeRowViewFlag).includes(viewFlag)
  ))
  for (let i = 0; isMatching && i < viewFlags.length; i++) {
    const viewFlag = viewFlags[i]
    isMatching = isMatching && viewFlag !== undefined && viewFlagFilters[viewFlag](timeRow, currentSiteId)
  }
  
  return isMatching
}

export const isMatchingSearchFilter = (
  check: TimeRow.AsObject | CollapsibleTimeGroup,
  searchFilter: SearchFilter,
  currentSiteId: SiteId | undefined,
): boolean => {
  if (isTimeRow(check)) {
    return (
      // matches if at least one of the filters matches
      searchFilter.activities.some(activity => check.activity.toLocaleLowerCase().includes(activity.toLocaleLowerCase())) ||
      searchFilter.descriptions.some(description => check.description.toLocaleLowerCase().includes(description.toLocaleLowerCase())) ||
      searchFilter.tags.some(tag => isMatchingTag(tag, check, currentSiteId)) ||
      searchFilter.activityTypes.some(activityType => isMatchingActivityType(activityType, check, currentSiteId)) ||
      searchFilter.tagCategories.some(tagCategory => isMatchingTagCategory(tagCategory, check, currentSiteId))
    )
  }
  
  const {
    timeGroup,
    timeRows,
  } = check
  
  return (
    searchFilter.activities.some(activity => (
      timeGroup.name?.toLocaleLowerCase().includes(activity.toLocaleLowerCase())
      || timeGroup.narrative.toLocaleLowerCase().includes(activity.toLocaleLowerCase())
      || timeRows.some(timeRow => timeRow.activity.toLocaleLowerCase().includes(activity.toLocaleLowerCase()))
    )) ||
    searchFilter.descriptions.some(description => (
      timeGroup.name?.toLocaleLowerCase().includes(description.toLocaleLowerCase())
      || timeGroup.narrative.toLocaleLowerCase().includes(description.toLocaleLowerCase())
      || timeRows.some(timeRow => timeRow.description.toLocaleLowerCase().includes(description.toLocaleLowerCase()))
    )) ||
    searchFilter.tags.some(tag => isMatchingTag(tag, timeGroup, currentSiteId)) ||
    searchFilter.activityTypes.some(activityType => isMatchingActivityType(activityType, timeGroup, currentSiteId)) ||
    searchFilter.tagCategories.some(tagCategory => isMatchingTagCategory(tagCategory, timeGroup, currentSiteId))
  )
}

export const isMatchingToolbarAndSearchFilters = (
  check: TimeRow.AsObject | CollapsibleTimeGroup,
  toolbar: ToolbarState,
  currentSiteId?: SiteId,
): boolean => {
  // Toolbar filters
  let isMatching = isTimeRow(check) ? isMatchingToolbarFilters(check, toolbar, currentSiteId) : true
  
  // Text search
  if (isMatching && isSearchNotEmpty(toolbar.searchTerms)) {
    const searchFilter = getMergedFilter(toolbar.searchTerms, currentSiteId)
    isMatching = isMatchingSearchFilter(
      check,
      searchFilter,
      currentSiteId,
    )
  }
  
  return isMatching
}

export const isMatchingShowSelectedItemToggle = (
  check: TimeRow.AsObject,
  showSelectedTimeRows: boolean,
  activeTimeRowIds: TimeRowId[],
): boolean => {
  return showSelectedTimeRows
    ? activeTimeRowIds.includes(check.id)
    : true
}
